import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = { class: "products__single-product q-p-1 q-position-relative q-d-flex" }
const _hoisted_3 = { class: "products__single-product-image-container q-col-4 q-col-lg-5 q-p-0" }
const _hoisted_4 = ["src", "alt", "width", "height"]
const _hoisted_5 = { class: "products__single-product-info q-col-8 q-col-lg-7 q-px-2 q-text-black q-text-start" }
const _hoisted_6 = { class: "products__single-product-title q-p-0 q-m-0 q-fw-semibold" }
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = ["href"]

import { computed, inject, ref, toRefs } from 'vue';
import { generateStaticImage, addDisplayMacros, formatPrice, parsePrice, getDiscount, getRealPrice } from '@/core/utils';
import { createTemplate, validateTemplate } from '@/core/Template';
import { SmartSearchConfigInterface } from '@/core/types';
import AutocompletePriceBox from '@/components/AutocompletePopup/AutocompletePriceBox.vue';
import { useSearchQueryStore } from '@/store/SearchQueryStore';
import { storeToRefs } from 'pinia';
import { highlightText } from '@/core/highlight';

// Rozszerzamy props o customTemplate, który będzie zawierał HTML szablonu z endpointu

export default /*@__PURE__*/_defineComponent({
  __name: 'SingleProduct',
  props: ['result', 'customTemplate'],
  setup(__props) {

const props = __props;
const { link, link_click, variants, id } = toRefs(props.result);
const firstVariant = ref(variants.value[0]);

const searchQueryStore = useSearchQueryStore();
const { searchQuery } = storeToRefs(searchQueryStore);
const highlightedTitle = computed(() => highlightText(
  props.result.title ?? '',
  searchQuery.value ?? '',
  'products__single-product-title-highlight',
));


// Pobranie konfiguracji
const SmartSearchConfig = ref(inject<SmartSearchConfigInterface>('SmartSearchConfig')!);
const { width, height } = SmartSearchConfig.value.onfocus.productImageSize;
const { useQuarticImages } = SmartSearchConfig.value.serp.productImageSize;
const { currency: currencyCode, removeDecimals } = SmartSearchConfig.value.appConfiguration;

// Obliczanie ścieżki do obrazu
const staticImage = computed(() => {
  const imageLink = firstVariant.value.image_link;
  if (!useQuarticImages) {
    return imageLink;
  }
  return imageLink
      ? generateStaticImage(imageLink, width, height)
      : generateStaticImage('https://static.quartic.com.pl/img/no-image/no-image.png', width, height);
});

// Obsługa linku
const actualLink = ref(link.value);
const handleClick = () => {
  actualLink.value = link_click.value.replace(/^http:/, 'https:');
};

// Dodatkowe funkcje i obliczenia dla makr
const priceData = computed(() => {
  const customMapping = window._QONizer?.customSmartSearchConfig?.customPriceMapping;

  const priceKey = customMapping?.price || 'price';
  const oldPriceKey = customMapping?.oldPrice || 'old_price';
  const salePriceKey = customMapping?.salePrice || 'sale_price';

  const v = firstVariant.value;
  const rawPrice = v[priceKey];
  const rawOldPrice = v[oldPriceKey];
  const rawSalePrice = v[salePriceKey];

  const priceToParse = (typeof rawPrice === 'string' || typeof rawPrice === 'number') ? rawPrice : v.price;
  const oldPriceToParse = (typeof rawOldPrice === 'string' || typeof rawOldPrice === 'number') ? rawOldPrice : v.old_price;
  const salePriceToParse = (typeof rawSalePrice === 'string' || typeof rawSalePrice === 'number') ? rawSalePrice : v.sale_price;

  const secondPrice = (oldPriceToParse || salePriceToParse) as string | number | null;
  const parsedPrice = parsePrice(priceToParse as string | number | null);
  const parsedOldPrice = parsePrice(secondPrice);
  const realPrices = getRealPrice(parsedPrice, parsedOldPrice);

  return {
    price: realPrices.price,
    oldPrice: realPrices.oldPrice,
    parsedPrice,
    parsedOldPrice
  };
});

const omnibusPrice = computed(() => parsePrice(firstVariant.value.omnibus_price));

const discountValue = computed(() => {
  const { price, oldPrice, parsedPrice, parsedOldPrice } = priceData.value;
  if (parsedPrice && parsedOldPrice && parsedPrice >= parsedOldPrice) {
    return null;
  }
  return getDiscount(price, oldPrice);
});

const discount = computed(() => discountValue.value);

const formattedPrice = computed(() => formatPrice(priceData.value.price, currencyCode, removeDecimals));
const formattedPriceOld = computed(() => formatPrice(priceData.value.oldPrice, currencyCode, removeDecimals));
const formattedOmnibusPrice = computed(() => formatPrice(omnibusPrice.value, currencyCode, removeDecimals));

// Funkcja przygotowująca czysty link z modyfikacją protokołu
const prepareClearLink = (link: string, clickLink: string) => {
  return ` href="${link}" data-href="${clickLink.replace(/^http:/, 'https:')}" onmousedown="return this.href = this.getAttribute('data-href');" ontouchstart="return this.href = this.getAttribute('data-href');" `;
};

// Mechanizm generowania HTML przy użyciu customTemplate
const generatedHtml = computed(() => {
  if (!props.customTemplate) return '';

  // Rozszerzamy dane produktu o dodatkowe właściwości (makra)
  const v = { ...firstVariant.value };
  v.discount = discount.value ? `- ${discount.value}%` : null;
  v.image_link_cdn = staticImage.value;
  v.price_formatted = formattedPrice.value;
  v.price_number = priceData.value.price;
  v.old_price_formatted = formattedPriceOld.value;
  v.old_price_number = priceData.value.oldPrice;
  v.omnibus_price_formatted = formattedOmnibusPrice.value;
  v.omnibus_price_number = omnibusPrice.value;
  v.id = id.value;
  v.link_click = link_click.value;
  v.clear_link = prepareClearLink(link.value, link_click.value);

  // Możemy dodatkowo modyfikować obiekt za pomocą funkcji addDisplayMacros
  addDisplayMacros(v);

  return createTemplate(props.customTemplate, v, variants.value);
});

return (_ctx: any,_cache: any) => {
  return (__props.customTemplate)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        innerHTML: generatedHtml.value,
        class: "products__single-product-container q-col-sm-12 q-col-lg-6"
      }, null, 8, _hoisted_1))
    : (_openBlock(), _createElementBlock("div", {
        key: __props.result.id,
        class: "products__single-product-container q-col-sm-12 q-col-lg-6"
      }, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("img", {
              class: "products__single-product-image q-p-1 q-img-fluid",
              src: staticImage.value,
              alt: __props.result.title,
              width: _unref(width),
              height: _unref(height)
            }, null, 8, _hoisted_4)
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("p", _hoisted_6, [
              _createElementVNode("span", { innerHTML: highlightedTitle.value }, null, 8, _hoisted_7)
            ]),
            _createVNode(AutocompletePriceBox, { product: firstVariant.value }, null, 8, ["product"]),
            _createElementVNode("a", {
              href: actualLink.value,
              onClick: handleClick,
              class: "q-stretched-link"
            }, null, 8, _hoisted_8)
          ])
        ])
      ]))
}
}

})